import trapInspectionService from '@/services/trapInspectionService'

export default  {
    namespaced: true,
    state: {
        networkError: false,
        locations: [],
        selectedPlant: null,
        selectedDivision: null,
        selectedLocation: null,
        selectedProductCategory: null
    },
    getters: {
        getPlantByID(state, getters) {
            return (id) => {
                return getters.getPlants.find(p => p.PlantID == id);
            }
        },
        getDivisionByID(state, getters) {
            return (id) => {
                return getters.getDivisions.find(d => d.DivisionID == id);
            }
        },
        getLocationByID(state, getters) {
            return (id) => {
                return getters.getLocations.find(l => l.LocationID == id);
            }
        },
        getSelectedPlantObject(state, getters) {
            return getters.getPlantByID(state.selectedPlant);
        },
        getSelectedDivisionObject(state, getters) {
            return getters.getDivisionByID(state.selectedDivision);
        },
        getSelectedLocationObject(state, getters) {
            return getters.getLocationByID(state.selectedLocation);
        },
        /**
         * Gets all distinct plants
         */
        getPlants(state) {
            if(state.locations) {                
                // Distinct Plants
                const map = new Map();
                const result = [];
                for(const location of state.locations) {
                    if(!map.has(location.Division.Plant.PlantID)) {
                        map.set(location.Division.Plant.PlantID);
                        result.push(location.Division.Plant);
                    }
                }

                return result.sort((a, b) => {
                    if(a.PlantName.toLowerCase() < b.PlantName.toLowerCase()) {
                        return -1;
                    }
    
                    if(a.PlantName.toLowerCase() > b.PlantName.toLowerCase()) {
                        return 1;
                    }
    
                    return 0;
                });
            }

            return [];
        },
        /**
         * Gets all distinct product categories
         */
         getProductCategories(state) {
            if(state.locations) {
                // Distinct Divisions                
                const map = new Map();
                const result = [];
                for(const location of state.locations) {
                    for(const category of location.ProductCategories) {
                        let key = `${category.ProductCategoryID}-${category.PlantID}`;
                        if(!map.has(key)) {
                            map.set(key);
                            result.push(category);
                        }
                    }
                }

                return result.sort((a, b) => {
                    if(a.ProductCategoryName.toLowerCase() < b.ProductCategoryName.toLowerCase()) {
                        return -1;
                    }
    
                    if(a.ProductCategoryName.toLowerCase() > b.ProductCategoryName.toLowerCase()) {
                        return 1;
                    }
    
                    return 0;
                });
            }

            return [];
        },
        /**
         * Gets all distinct divisions
         */
        getDivisions(state, getters) {
            if(state.locations) {
                const locations = getters.getFilteredLocations

                // Distinct Divisions                
                const map = new Map();
                const result = [];
                for(const location of locations) {
                    if(!map.has(location.Division.DivisionID)) {
                        map.set(location.Division.DivisionID);
                        result.push(location.Division);
                    }
                }

                return result.sort((a, b) => {
                    if(a.DivisionName.toLowerCase() < b.DivisionName.toLowerCase()) {
                        return -1;
                    }
    
                    if(a.DivisionName.toLowerCase() > b.DivisionName.toLowerCase()) {
                        return 1;
                    }
    
                    return 0;
                });
            }

            return [];
        },
        /**
         * Gets all distinct locations
         */
        getLocations(state) {
            if(state.locations) {
                return state.locations.sort((a, b) => {
                    if(a.LocationName.toLowerCase() < b.LocationName.toLowerCase()) {
                        return -1;
                    }
    
                    if(a.LocationName.toLowerCase() > b.LocationName.toLowerCase()) {
                        return 1;
                    }
    
                    return 0;
                });
            }

            return [];
        },
        /**
         * Returns product categories in the selected plant
         */
        getFilteredProductCategories(state, getters) {            
            let productCategories = getters.getProductCategories;

            return productCategories.filter(function(p) {
                if (state.selectedPlant) {
                    return p.PlantID === state.selectedPlant;
                }

                // no filtering? return everything
                return true;
            });
        },
        /**
         * Returns divisions in the selected plant
         */
        getFilteredDivisions(state, getters) {
            let divisions = getters.getDivisions;

            return divisions.filter(function(d) {
                if (state.selectedPlant) {
                    return d.Plant.PlantID === state.selectedPlant;
                }

                // no filtering? return everything
                return true;
            });
        },
        /**
         * Returns locations in the selected plant and/or division
         */
        getFilteredLocations(state, getters) {
            let locations = getters.getLocations;

            return locations.filter(function(l) {
                let match = true;

                if (state.selectedDivision) {
                    match = l.Division.DivisionID === state.selectedDivision;
                }
                else if (state.selectedPlant) {
                    match = l.Division.Plant.PlantID === state.selectedPlant;
                }

                if (state.selectedProductCategory) {
                    match &= l.ProductCategories.some(pc => pc.ProductCategoryID === state.selectedProductCategory)
                }
                
                return match;
            });
        }
    },
    mutations: {
        updateLocations(state, locations) {
            state.locations = locations;
        },
        setNetworkError(state, value){
            state.networkError = value;
        },
        setSelectedPlant (state, value) {
            state.selectedPlant = value;
            state.selectedProductCategory = null;
            state.selectedDivision = null;
            state.selectedLocation = null;
        },
        setSelectedDivision (state, value) {
            state.selectedDivision = value;            
            state.selectedLocation = null;
        },
        setSelectedLocation (state, value) {
            state.selectedLocation = value;
        },
        setSelectedProductCategory(state, value) {
            state.selectedProductCategory = value;
            state.selectedDivision = null;
            state.selectedLocation = null;
        }
    },
    actions: {
        async handleOffline({ commit }, options){
            try {
                await options.action();
                commit("setNetworkError", false);
            } catch(e){
                commit("setNetworkError", true);
            }
        },
        async loadLocations({ commit, dispatch }) {
            // Try to load from the server and if we go offline then just continue and try to load from indexdb.
            await dispatch("handleOffline", {
                async action(){
                    await trapInspectionService.fetchAndStoreLocations();
                }
            });

            // Load from indexdb
            let locations = await trapInspectionService.getLocations();
            commit('updateLocations', locations);
        }
    }
}